import { optimizeCss } from '@wix/css-optimization'
import {
	BeckyFunctionLibrary,
	BeckyRequestPayload,
	Experiments,
	ProcessOoiCssFunctionLib,
	SiteAssetsModule,
} from '../types/becky'
import { createFunctionLibrary } from '../functionLibrary'
import { resolveBeckyModel } from './helpers/resolveBeckyModel'
import { typedModelFactory } from './helpers/typedModelFactory'
import { CssInstanceModel } from '../types/css'
import { getStylableInstance } from './stylable'
import byRefModule from '../byRef'
import { BeckyTopology, FetchFn, MetricsReporter, ModuleFetcher } from '@wix/thunderbolt-becky-types'
import { maybeAppendErrorPages } from '../errorPages/getPayloadWithErrorPage'
import { withBeckyCache } from './utils/withBeckyCache'
import { getBeckyTopology } from './helpers/topologyBuilder'
import { processOoiCssImpl } from './helpers/ooiCssProcessor'
import LRU from 'lru-cache'
import { deStringifyExperiments } from './helpers/experimentsParser'

export const cssModelFactory = typedModelFactory<CssInstanceModel, ProcessOoiCssFunctionLib>(
	require('../carmi/cssModule.carmi')
)

const ooiCssModelFactory = require('../carmi/ooiCss.carmi')

export const getCssResult = async ({
	payload,
	beckyTopology,
	funcLib,
	moduleFetcher,
	fetchFn,
}: {
	payload: BeckyRequestPayload
	beckyTopology: BeckyTopology
	funcLib: BeckyFunctionLibrary
	moduleFetcher: ModuleFetcher
	fetchFn: FetchFn
}): Promise<CssInstanceModel> => {
	const beckyModel = await resolveBeckyModel({ payload, beckyTopology, moduleFetcher, fetchFn })
	const ooiCssModel = ooiCssModelFactory(beckyModel, funcLib)
	const processOoiCss = await processOoiCssImpl(ooiCssModel, {
		funcLib,
		moduleFetcher,
		payload,
	})
	return funcLib.runAsyncAndReport(
		() => new Promise((done) => cssModelFactory(beckyModel, { ...funcLib, processOoiCss, done })),
		'tb-css-model-calculation'
	)
}

const getCssResultWithStylableInstance = async (
	payload: BeckyRequestPayload,
	beckyTopology: BeckyTopology,
	experiments: Experiments,
	fetchFn: FetchFn,
	metricsReporter: MetricsReporter,
	funcLib: BeckyFunctionLibrary,
	moduleFetcher: ModuleFetcher
) => {
	const maybeStylableInstance = await getStylableInstance({
		payload,
		fetch: fetchFn,
		fileRepoUrl: beckyTopology.environmentTopology.fileRepoUrl,
		metricsReporter,
		cache: stylableCache,
		themeData: (await payload.pages.masterPage).data.theme_data.THEME_DATA,
		staticMediaUrl: beckyTopology.publicTopology.staticMediaUrl,
	})

	const cssResults = await getCssResult({
		payload: {
			stylableInstance: maybeStylableInstance,
			...payload,
		},
		beckyTopology,
		funcLib,
		moduleFetcher,
		fetchFn,
	})
	const css = cssResults.css

	try {
		return await optimizeCss(css, {
			variablize: experiments['specs.thunderbolt.optimizeCSS_variablize'] === true,
		})
	} catch (e) {
		metricsReporter.reportError(new Error(`Optimizing CSS failed: ${e}`))
	}

	return css
}

const stylableCache = new LRU<string, any>({ max: 20 })

const cssModule: SiteAssetsModule<string> = (moduleContext) => {
	const { httpClient, metricsReporter, topologyGetter, moduleFetcher } = moduleContext
	const fetchFn = withBeckyCache(httpClient.fetch, metricsReporter)
	const funcLib = createFunctionLibrary({
		fetch: fetchFn,
		metricsReporter,
	})
	const byRef = byRefModule(moduleContext)

	return {
		execute: async (payload) => {
			const experiments = deStringifyExperiments(payload.beckyExperiments)
			const beckyTopology = getBeckyTopology(
				topologyGetter,
				payload,
				experiments['specs.thunderbolt.relativeMediaRoot']
			)

			return byRef
				.execute(maybeAppendErrorPages(payload))
				.then((payloadWithResolvedRefs) =>
					getCssResultWithStylableInstance(
						payloadWithResolvedRefs,
						beckyTopology,
						experiments,
						fetchFn,
						metricsReporter,
						funcLib,
						moduleFetcher
					)
				)
		},
	}
}

export default cssModule
